import type { MessageKey } from "@ec2/i18n";
import type { InstallPlatform } from "./platform";

/**
 * The install walkthrough for each platform, as data: which mock-up scene to
 * show (components/install/MockDevice.tsx) and what the caption says. Only the
 * guide for the detected platform is ever rendered.
 *
 * The mock-ups are stylised, not screenshots — the steps are what has to be
 * right. Labels inside the scenes are the browsers' own wording per language
 * (ec2.install.ui.*), so the player looks for the words they will actually see.
 */

export type SceneId =
  | "ios-bottom-share" // Safari toolbar, share button
  | "ios-address-share" // Chrome/Edge/Firefox on iOS: share in the address bar
  | "ios-share-sheet" // share sheet, "Add to Home Screen" row
  | "ios-add-confirm" // the "Add" button top right
  | "home-screen" // the new icon on the home screen
  | "inapp-menu" // ⋯ in an app's built-in browser
  | "inapp-open-browser" // "Open in browser" in that menu
  | "android-menu" // ⋮ top right
  | "android-menu-install" // "Install app" / "Add to Home screen" in the menu
  | "android-install-dialog" // the system install dialog (Samsung's confirm)
  | "android-install-choice" // Chrome: "Install" vs "Create shortcut"
  | "firefox-menu" // ⋮ at the bottom right of Firefox's toolbar
  | "firefox-menu-add" // "Add app to Home screen" in Firefox's menu
  | "firefox-add-confirm" // the "Add" confirmation
  | "samsung-menu" // ☰ in Samsung Internet's bottom bar
  | "samsung-add-page" // "Add page to" → "Home screen"
  | "desktop-omnibox-install" // install icon at the right of the address bar
  | "desktop-install-dialog" // the browser's install dialog
  | "mac-file-menu" // Safari: File menu → "Add to Dock…"
  | "mac-add-dock"; // the "Add" button

export interface GuideStep {
  scene: SceneId;
  caption: MessageKey;
}

export type InstallGuide =
  | { kind: "steps"; steps: GuideStep[]; promptable: boolean; noteKey?: MessageKey }
  | { kind: "installed" }
  | { kind: "unsupported"; messageKey: MessageKey };

const OPEN_FROM_HOME: GuideStep = {
  scene: "home-screen",
  caption: "ec2.install.step.openFromHome",
};

const IOS_ADD: GuideStep[] = [
  { scene: "ios-share-sheet", caption: "ec2.install.step.iosAddToHome" },
  { scene: "ios-add-confirm", caption: "ec2.install.step.iosConfirm" },
  OPEN_FROM_HOME,
];

/**
 * Chrome (and Edge/Opera/Brave) on Android. Current Chrome no longer installs
 * straight from the menu: "Add to Home screen" asks whether to INSTALL or only
 * create a shortcut — the shortcut is not the app (no full screen, no push),
 * so the guide says which one to pick (field report, 2026-10-19).
 */
const ANDROID_MENU: GuideStep[] = [
  { scene: "android-menu", caption: "ec2.install.step.androidMenu" },
  { scene: "android-menu-install", caption: "ec2.install.step.androidInstall" },
  { scene: "android-install-choice", caption: "ec2.install.step.androidChoose" },
  OPEN_FROM_HOME,
];

/**
 * Firefox on Android: its toolbar — menu included — sits at the BOTTOM by
 * default, and the entry is "Add app to Home screen" (field report,
 * 2026-10-19; the guide used to show Chrome's top-right menu).
 */
const FIREFOX_ANDROID: GuideStep[] = [
  { scene: "firefox-menu", caption: "ec2.install.step.firefoxMenu" },
  { scene: "firefox-menu-add", caption: "ec2.install.step.firefoxAddApp" },
  { scene: "firefox-add-confirm", caption: "ec2.install.step.firefoxConfirm" },
  OPEN_FROM_HOME,
];

export const INSTALL_GUIDES: Record<InstallPlatform, InstallGuide> = {
  installed: { kind: "installed" },
  "ios-safari": {
    kind: "steps",
    promptable: false,
    steps: [{ scene: "ios-bottom-share", caption: "ec2.install.step.iosShareBottom" }, ...IOS_ADD],
    noteKey: "ec2.install.note.iosNewSafari",
  },
  "ios-other": {
    kind: "steps",
    promptable: false,
    steps: [
      { scene: "ios-address-share", caption: "ec2.install.step.iosShareAddress" },
      ...IOS_ADD,
    ],
  },
  "ios-inapp": {
    kind: "steps",
    promptable: false,
    steps: [
      { scene: "inapp-menu", caption: "ec2.install.step.inappMenu" },
      { scene: "inapp-open-browser", caption: "ec2.install.step.inappOpenSafari" },
      { scene: "ios-bottom-share", caption: "ec2.install.step.iosShareBottom" },
      ...IOS_ADD,
    ],
    noteKey: "ec2.install.note.inapp",
  },
  // A tap on our button opens the browser's own dialog when it has offered one;
  // the menu steps are the fallback when it has not (yet).
  "android-chromium": { kind: "steps", promptable: true, steps: ANDROID_MENU },
  "android-firefox": {
    kind: "steps",
    promptable: false,
    steps: FIREFOX_ANDROID,
    noteKey: "ec2.install.note.firefoxToolbarTop",
  },
  "android-samsung": {
    kind: "steps",
    promptable: true,
    steps: [
      { scene: "samsung-menu", caption: "ec2.install.step.samsungMenu" },
      { scene: "samsung-add-page", caption: "ec2.install.step.samsungAddPage" },
      { scene: "android-install-dialog", caption: "ec2.install.step.androidConfirm" },
      OPEN_FROM_HOME,
    ],
  },
  "android-inapp": {
    kind: "steps",
    promptable: false,
    steps: [
      { scene: "inapp-menu", caption: "ec2.install.step.inappMenu" },
      { scene: "inapp-open-browser", caption: "ec2.install.step.inappOpenBrowser" },
      ...ANDROID_MENU,
    ],
    noteKey: "ec2.install.note.inapp",
  },
  "desktop-chromium": {
    kind: "steps",
    promptable: true,
    steps: [
      { scene: "desktop-omnibox-install", caption: "ec2.install.step.desktopOmnibox" },
      { scene: "desktop-install-dialog", caption: "ec2.install.step.desktopConfirm" },
    ],
  },
  "macos-safari": {
    kind: "steps",
    promptable: false,
    steps: [
      { scene: "mac-file-menu", caption: "ec2.install.step.macFileMenu" },
      { scene: "mac-add-dock", caption: "ec2.install.step.macAddDock" },
    ],
  },
  "desktop-firefox": { kind: "unsupported", messageKey: "ec2.install.unsupported.firefox" },
  unknown: { kind: "unsupported", messageKey: "ec2.install.unsupported.unknown" },
};
